import type {
  Address,
  Id,
  IsoDateTime,
  Json,
  JsonObject,
  Mirrored,
} from "../primitives";

/**
 * A place stock sits.
 *
 * Mirrored from the channel's locations, because that is where inventory
 * levels are anchored and an ERP-invented warehouse would have nothing to
 * write through to.
 */
export interface Warehouse extends Mirrored {
  id: Id;
  name: string;
  /** Short code the UI uses in dense tables. `"MUM"`, `"JAI"`. */
  code: string;
  address: Address | null;
  isActive: boolean;
  /**
   * Whether this location can be shipped from. A location that only receives
   * returns still holds stock but should not be offered as a dispatch source.
   */
  fulfilsOrders: boolean;
}

/**
 * How much of one variant sits at one warehouse.
 *
 * `available` is the channel's number and the ERP never computes it — the
 * write path adjusts it at the channel and reads the result back.
 */
/**
 * The stock Shopify counts as unavailable at a location: on the shelf, not
 * for sale. Shopify's inventory screen shows the sum as "Unavailable" and
 * breaks it down into these four.
 */
export interface StockUnavailable {
  damaged: number;
  qualityControl: number;
  safetyStock: number;
  /** Held for something other than an order (a draft order, a reservation). */
  reserved: number;
}

export const UNAVAILABLE_STATES = [
  "damaged",
  "qualityControl",
  "safetyStock",
  "reserved",
] as const;

export const UNAVAILABLE_LABELS: Record<
  (typeof UNAVAILABLE_STATES)[number],
  string
> = {
  damaged: "Damaged",
  qualityControl: "Quality control",
  safetyStock: "Safety stock",
  reserved: "Reserved",
};

/** What Shopify's screen calls Unavailable: damaged + quality control + safety stock + reserved. */
export function unavailableTotal(unavailable: StockUnavailable): number {
  return (
    unavailable.damaged +
    unavailable.qualityControl +
    unavailable.safetyStock +
    unavailable.reserved
  );
}

/**
 * On hand, as Shopify defines it (shopify.dev, "Manage inventory quantities and
 * states", checked 2026-10): the total physically stocked at a location, which
 * equals available + committed + reserved + damaged + safety stock + quality
 * control. `incoming` is not part of it, and neither is the ERP's own
 * in-transit figure.
 */
export function onHandOf(level: {
  available: number;
  committed: number | null;
  unavailable: StockUnavailable;
}): number {
  return (
    level.available +
    (level.committed ?? 0) +
    unavailableTotal(level.unavailable)
  );
}

export interface InventoryLevel {
  id: Id;
  variantId: Id;
  warehouseId: Id;
  available: number;
  /** Damaged, quality control, safety stock and reserved: Shopify's Unavailable, broken down. */
  unavailable: StockUnavailable;
  /** Everything physically there: `onHandOf` of the figures above. */
  onHand: number;
  /**
   * Committed to orders that have not shipped. Null where the channel does
   * not report it rather than zero, because zero is a claim and null is not.
   */
  committed: number | null;
  /**
   * Dispatched from another warehouse and not yet received here.
   *
   * *Amended 2026-09-28 (#172):* computed from open `stock_transfer_lines`
   * (`qty_dispatched - qty_received`, summed per variant and warehouse) —
   * ERP-owned, per FR-WHS-6/FR-WHS-7 and spec §8.2's "in-transit stock is
   * ERP-owned and nowhere in Shopify". The 2026-09-12 correction that this
   * comment previously carried — that this is Shopify's own `incoming`
   * quantity — contradicted both and has been reverted in the spec; see its
   * §8 amendment note. `inventory_levels.incoming` is still mirrored, but it
   * is a distinct Shopify figure (inbound purchase orders and native
   * transfers) that this design's own transfers never touch, so nothing
   * here reads it as "in transit" any more. The fixture adapter derives this
   * from `Transfer` rows for the same reason it always did: there is
   * nothing else to derive it from, fixture or real.
   *
   * The real adapter's Stock Levels / Low Stock rows (`InventoryRow`, spec
   * FR-INV-2) still read `inventory_levels.incoming` for this field as of
   * #172 — reconciling that display with this corrected definition is
   * tracked separately and is not this field's contract.
   */
  inTransit: number;
  /** When this figure last came back from the channel (FR-INV-5). */
  lastSyncedAt: IsoDateTime;
}

/**
 * Where a transfer has got to.
 *
 * `draft` is editable; everything from `dispatched` onward has already moved
 * stock at the channel and is not.
 */
export type TransferStatus =
  "draft" | "dispatched" | "partially_received" | "received" | "cancelled";

/** One variant's journey inside a transfer. */
export interface TransferLine {
  id: Id;
  transferId: Id;
  variantId: Id;
  /** What was sent. Fixed at dispatch. */
  quantityDispatched: number;
  /**
   * What arrived. Accumulates across partial receipts, and may end below
   * `quantityDispatched` — the shortfall is the whole reason receipts are
   * recorded separately rather than assumed.
   */
  quantityReceived: number;
  /**
   * Why the latest receipt on this line fell short of what remained
   * (FR-WHS-8) — required whenever a receipt does not fully close out the
   * line, null otherwise. The line's own current-state snapshot; the full
   * history of every receipt round and its own reason is the transfer's
   * event history, not this field.
   */
  discrepancyReason: string | null;
}

/**
 * A stock movement between two warehouses, owned by the ERP.
 *
 * Shopify has no concept of this. It is expressed there as two adjustments —
 * a decrement at the source on dispatch, an increment at the destination on
 * receipt — and the gap between them is what `inTransit` reports.
 */
export interface Transfer {
  id: Id;
  /** Human reference. `"TR-2026-0043"`. */
  reference: string;
  fromWarehouseId: Id;
  toWarehouseId: Id;
  status: TransferStatus;
  note: string | null;
  createdByUserId: Id;
  createdAt: IsoDateTime;
  dispatchedAt: IsoDateTime | null;
  /** Set when the last line is fully received, or when closed short. */
  completedAt: IsoDateTime | null;
  lines: TransferLine[];
}

/**
 * One row of a transfer's full timestamped history (FR-WHS-9) — a status
 * transition, a line dispatched, a line's dispatch compensated after a later
 * line failed, a receipt recorded, a cancellation. Append-only, the same
 * shape `OrderEvent` and `TaskEvent` already use.
 */
export interface TransferEvent {
  id: Id;
  transferId: Id;
  occurredAt: IsoDateTime;
  actorUserId: Id | null;
  eventType: string;
  field: string | null;
  before: Json | null;
  after: Json | null;
}

/**
 * Why stock changed outside a sale or a transfer (FR-INV-7).
 *
 * Extensible by design — this is the shipped set, not a closed vocabulary,
 * and a reason the team invents next month should not be a schema change.
 */
export type AdjustmentReason =
  | "rto_received"
  | "damaged"
  | "lost"
  | "stock_take"
  | "correction"
  | (string & {});

/**
 * Whether the channel accepted a write.
 *
 * A rejected adjustment is a row, not a log line: the user who made it has to
 * see that it failed and why, and the local stock figure has to be unchanged
 * (FR-INV-9). Nobody should have to read a container log to find out why a
 * stock adjustment did not take.
 */
export type WriteStatus = "pending" | "accepted" | "rejected";

/**
 * A manual correction to stock at one warehouse.
 *
 * Recorded as a delta rather than a new absolute, so two adjustments landing
 * close together compose instead of one silently discarding the other.
 */
export interface StockAdjustment {
  id: Id;
  variantId: Id;
  warehouseId: Id;
  /** Signed. Negative writes stock off. */
  delta: number;
  reason: AdjustmentReason;
  note: string | null;
  actorUserId: Id;
  status: WriteStatus;
  /** The channel's own words when it refused. Shown to the user verbatim. */
  error: string | null;
  createdAt: IsoDateTime;
  /** When the channel accepted or refused. Null while pending. */
  settledAt: IsoDateTime | null;
}

/**
 * Where a channel write stands (spec §8 `channel_writes.status`).
 *
 * Its own vocabulary rather than `WriteStatus`: the spec names these three,
 * and a stock adjustment's accepted/rejected is a different record's state.
 */
export type ChannelWriteStatus = "pending" | "succeeded" | "failed";

/**
 * One attempt to write something to a sales channel.
 *
 * The audit trail and the echo filter in one row, per §6.6. Every ERP edit
 * that crosses the channel boundary lands here before it lands anywhere else,
 * which is what lets a rejected write be a row someone can read rather than a
 * line in a container log.
 *
 * `fingerprint` is `(entity, normalised payload)`. It is how ingest recognises
 * its own write coming back as a webhook and declines to re-ingest it.
 */
export interface ChannelWrite {
  id: Id;
  channelId: Id;
  /** `"order"`, `"variant"`, `"inventory_level"`. */
  entityType: string;
  entityId: Id;
  /** What was attempted. `"orderUpdate"`, `"inventoryAdjustQuantities"`. */
  mutation: string;
  /** What was sent. Kept for the audit trail, never rendered by default. */
  request: JsonObject;
  /** What came back. Null while pending, and for a write refused unsent. */
  response: JsonObject | null;
  fingerprint: string;
  status: ChannelWriteStatus;
  /**
   * The channel's own words when it refused.
   *
   * Shown verbatim. A row that says "failed" without saying why is exactly
   * the design defect the expected-failures-are-rows rule exists to prevent.
   */
  error: string | null;
  actorUserId: Id | null;
  createdAt: IsoDateTime;
  /** When the channel answered. Null while pending. */
  completedAt: IsoDateTime | null;
}
